import { useState, useEffect, useRef, useCallback } from "react";
import type { LocalAudioTrack } from "twilio-video";

/**
 * Hook that monitors the audio level from a LocalAudioTrack.
 * Uses Web Audio API to analyze the microphone input level.
 *
 * @param audioTrack - The Twilio LocalAudioTrack to monitor
 * @param enabled - Whether monitoring is enabled (default: true)
 * @returns A number between 0 and 1 representing the current audio level
 *
 * @example
 * ```tsx
 * const audioLevel = useAudioLevel(localAudioTrack);
 * // audioLevel is 0-1, where 0 is silence and 1 is max volume
 * ```
 */
export function useAudioLevel(audioTrack: LocalAudioTrack | null, enabled: boolean = true): number {
  const [audioLevel, setAudioLevel] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isRunningRef = useRef(false);

  // Calculate RMS (Root Mean Square) for accurate volume level
  const calculateRMS = useCallback((dataArray: Uint8Array): number => {
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
      // Convert from 0-255 to -1 to 1 range (128 is silence)
      const sample = dataArray[i] ?? 128;
      const normalized = (sample - 128) / 128;
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / dataArray.length);
    // Scale up significantly for better visual response (RMS values are typically very small)
    return Math.min(rms * 5, 1);
  }, []);

  useEffect(() => {
    if (!audioTrack || !enabled) {
      setAudioLevel(0);
      return;
    }

    // Get the underlying MediaStreamTrack
    const mediaStreamTrack = audioTrack.mediaStreamTrack;
    if (!mediaStreamTrack) {
      console.warn("[useAudioLevel] No mediaStreamTrack available on audioTrack");
      return;
    }

    // Create a MediaStream from the track
    const mediaStream = new MediaStream([mediaStreamTrack]);

    // Create AudioContext
    const audioContext = new AudioContext();

    // Resume AudioContext if suspended (browser autoplay policy)
    if (audioContext.state === "suspended") {
      audioContext.resume().catch((err) => {
        console.warn("[useAudioLevel] Failed to resume AudioContext:", err);
      });
    }

    const analyser = audioContext.createAnalyser();
    // Use smaller FFT for faster updates
    analyser.fftSize = 256;
    // Higher smoothing = slower decay, makes the visual response more stable
    analyser.smoothingTimeConstant = 0.5;

    // Connect the media stream to the analyser
    const source = audioContext.createMediaStreamSource(mediaStream);
    source.connect(analyser);

    // Store refs for cleanup
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    sourceRef.current = source;
    isRunningRef.current = true;

    // Create data array for time domain data (waveform)
    const dataArray = new Uint8Array(analyser.fftSize);

    // Animation loop to read audio levels
    const updateLevel = () => {
      if (!isRunningRef.current || !analyserRef.current) return;

      // Use time domain data for better voice level detection
      analyserRef.current.getByteTimeDomainData(dataArray);

      // Calculate RMS for accurate volume representation
      const level = calculateRMS(dataArray);
      setAudioLevel(level);

      // Continue the loop
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    // Start the animation loop
    animationFrameRef.current = requestAnimationFrame(updateLevel);

    // Cleanup function
    return () => {
      isRunningRef.current = false;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // Ignore close errors
        });
        audioContextRef.current = null;
      }

      analyserRef.current = null;
      setAudioLevel(0);
    };
  }, [audioTrack, enabled, calculateRMS]);

  return audioLevel;
}
